import React, { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronRight, Clock } from "lucide-react";
import { Stage, useStageMotion } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];
const usd = (n) => "$" + Math.round(n).toLocaleString("en-US");
const num = (n) => Math.round(n).toLocaleString("en-US");

// Count-up on first appear, smooth tween on change. Respects reduced motion.
function useAnimatedNumber(value, { reduce, duration = 500, initialDuration = 650, initialDelay = 400 }) {
  const [display, setDisplay] = useState(reduce ? value : 0);
  const fromRef = useRef(reduce ? value : 0);
  const firstRef = useRef(true);
  const rafRef = useRef();
  const toRef = useRef();

  useEffect(() => {
    if (reduce) {
      setDisplay(value);
      fromRef.current = value;
      return;
    }
    const from = fromRef.current;
    const to = value;
    toRef.current = to;
    const isFirst = firstRef.current;
    const dur = isFirst ? initialDuration : duration;
    const delay = isFirst ? initialDelay : 0;
    firstRef.current = false;

    let startTime = null;
    const step = (now) => {
      if (startTime === null) startTime = now;
      const t = Math.min((now - startTime) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const cur = from + (to - from) * eased;
      setDisplay(cur);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    const startTimer = setTimeout(() => {
      rafRef.current = requestAnimationFrame(step);
    }, delay);

    return () => {
      clearTimeout(startTimer);
      cancelAnimationFrame(rafRef.current);
      fromRef.current = toRef.current;
    };
  }, [value, reduce, duration, initialDuration, initialDelay]);

  return display;
}

const Slider = ({ label, value, display, min, max, step, onChange, testId }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2.5">
        <label htmlFor={testId} className="font-sans text-sm text-ink">
          {label}
        </label>
        <span
          data-testid={`${testId}-value`}
          className="font-mono text-sm text-ink tabular-nums"
        >
          {display}
        </span>
      </div>
      <input
        id={testId}
        data-testid={testId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="range-lex"
        style={{
          background: `linear-gradient(to right, #1E5BFF 0%, #1E5BFF ${pct}%, #E3E1DE ${pct}%, #E3E1DE 100%)`,
        }}
      />
    </div>
  );
};

const AssumptionField = ({ label, value, onChange, suffix, testId }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="font-sans text-[13px] text-ink-soft">{label}</span>
    <div className="flex items-center gap-1">
      <input
        data-testid={testId}
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="num-lex w-14 bg-transparent border-b border-hairline focus:border-accent text-right font-mono text-sm text-ink py-1 outline-none transition-colors"
      />
      {suffix ? (
        <span className="font-mono text-sm text-ink-muted">{suffix}</span>
      ) : null}
    </div>
  </div>
);

export const CostSlide = ({ slide }) => {
  const { container, item } = useStageMotion();
  const reduce = useReducedMotion();

  const [callsPerDay, setCallsPerDay] = useState(40);
  const [unansweredPct, setUnansweredPct] = useState(15);
  const [avgFee, setAvgFee] = useState(20000);

  const [newClientShare, setNewClientShare] = useState(25);
  const [conversionRate, setConversionRate] = useState(33);
  const [lostForGood, setLostForGood] = useState(50);
  const [businessDays, setBusinessDays] = useState(22);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const { lostFeesYr, lostCasesYr, missedPerMonth } = useMemo(() => {
    const monthlyCalls = callsPerDay * businessDays;
    const missed = monthlyCalls * (unansweredPct / 100);
    const lostMo =
      missed * (newClientShare / 100) * (conversionRate / 100) * (lostForGood / 100);
    const lostYr = lostMo * 12;
    return {
      lostFeesYr: lostYr * avgFee,
      lostCasesYr: lostYr,
      missedPerMonth: missed,
    };
  }, [callsPerDay, unansweredPct, avgFee, newClientShare, conversionRate, lostForGood, businessDays]);

  const animatedFees = useAnimatedNumber(lostFeesYr, { reduce });

  return (
    <Stage testId={`slide-${slide?.n ?? 2}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(24px,5%,52px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>The cost of inaction</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            03 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-5 max-w-[16ch]"
        >
          What every missed call is really costing you.
        </motion.h1>

        {/* Body */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-x-[clamp(32px,5%,72px)] gap-y-8 mt-7 min-h-0">
          {/* LEFT — controls */}
          <motion.div variants={item} className="flex flex-col gap-6">
            <div className="flex flex-col gap-5">
              <Slider
                testId="slider-calls"
                label="Inbound calls per day"
                min={10}
                max={200}
                step={5}
                value={callsPerDay}
                display={num(callsPerDay)}
                onChange={setCallsPerDay}
              />
              <Slider
                testId="slider-unanswered"
                label="Calls that go unanswered"
                min={2}
                max={45}
                step={1}
                value={unansweredPct}
                display={`${unansweredPct}%`}
                onChange={setUnansweredPct}
              />
              <Slider
                testId="slider-fee"
                label="Average fee per signed case"
                min={5000}
                max={100000}
                step={1000}
                value={avgFee}
                display={usd(avgFee)}
                onChange={setAvgFee}
              />
            </div>

            {/* Assumptions drawer */}
            <div>
              <button
                data-testid="assumptions-toggle"
                onClick={() => setDrawerOpen((o) => !o)}
                aria-expanded={drawerOpen}
                className="inline-flex items-center gap-2 font-sans text-sm text-ink-soft hover:text-ink transition-colors"
              >
                <span className="border-b border-dashed border-ink-muted pb-0.5">
                  Assumptions
                </span>
                <ChevronRight
                  className={`w-4 h-4 text-accent transition-transform duration-300 ${
                    drawerOpen ? "rotate-90" : ""
                  }`}
                />
              </button>

              <AnimatePresence initial={false}>
                {drawerOpen ? (
                  <motion.div
                    data-testid="assumptions-drawer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: reduce ? 0.001 : 0.35, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                      <AssumptionField
                        testId="assumption-newclient"
                        label="New-client share of calls"
                        value={newClientShare}
                        onChange={setNewClientShare}
                        suffix="%"
                      />
                      <AssumptionField
                        testId="assumption-conversion"
                        label="Inquiry → signed rate"
                        value={conversionRate}
                        onChange={setConversionRate}
                        suffix="%"
                      />
                      <AssumptionField
                        testId="assumption-lost"
                        label="Missed callers lost for good"
                        value={lostForGood}
                        onChange={setLostForGood}
                        suffix="%"
                      />
                      <AssumptionField
                        testId="assumption-days"
                        label="Business days / month"
                        value={businessDays}
                        onChange={setBusinessDays}
                      />
                    </div>
                    <p className="mt-4 font-sans text-xs text-ink-muted leading-relaxed max-w-md">
                      All figures are estimates — your firm will vary. Adjust any
                      assumption to match your own numbers.
                    </p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* RIGHT — reveal */}
          <motion.div
            variants={item}
            className="flex flex-col justify-center lg:border-l lg:border-hairline lg:pl-[clamp(28px,5%,64px)]"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
              Fees leaving, every year
            </p>
            <div
              data-testid="cost-annual-fees"
              className="font-display font-semibold text-5xl sm:text-6xl leading-none text-accent tabular-nums mt-3"
            >
              {usd(animatedFees)}
            </div>
            <p className="font-sans text-base text-ink-soft mt-3 max-w-sm">
              walking out the door — to the firm that picked up.
            </p>

            <div className="flex gap-10 mt-7">
              <div>
                <div
                  data-testid="cost-cases-lost"
                  className="font-mono text-2xl text-ink tabular-nums"
                >
                  {num(lostCasesYr)}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mt-1">
                  Signable cases lost / yr
                </div>
              </div>
              <div>
                <div
                  data-testid="cost-missed-calls"
                  className="font-mono text-2xl text-ink tabular-nums"
                >
                  {num(missedPerMonth)}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mt-1">
                  Missed calls / month
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 mt-7 rounded-xl border border-accent/20 bg-accent/[0.06] px-4 py-3.5 max-w-md">
              <Clock className="w-4 h-4 text-accent mt-0.5 shrink-0" />
              <p className="font-sans text-[13px] text-ink-soft leading-relaxed">
                The best-responding firms call back in under 2 minutes. After hours
                and overflow are where cases quietly slip away.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Punchline */}
        <motion.div
          variants={item}
          className="border-t border-hairline pt-5 mt-2"
        >
          <p className="font-display font-medium text-xl sm:text-2xl leading-snug text-ink">
            You're not short on leads. You're short on{" "}
            <em className="italic text-accent">
              hands to answer them.
            </em>
          </p>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default CostSlide;
